// src/App.jsx
import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar'
import { format, parseISO, startOfWeek, getDay, addWeeks } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import 'react-big-calendar/lib/css/react-big-calendar.css'

import planitLogo from './icons/planit-p.svg'

const BRAND_YELLOW = '#FDE68A'
const BRAND_TEXT_DARK = '#0B1220'

const CATEGORY_COLORS = {
  Fitness:          '#3B82F6',
  Sports:           '#EF4444',
  Music:            '#22C55E',
  'Arts & Theatre': '#FDE68A',
  Miscellaneous:    '#6B7280',
  Uncategorized:    '#93C5FD',
}

const locales = { 'en-US': require('date-fns/locale/en-US') }
const localizer = dateFnsLocalizer({ format, parse: parseISO, startOfWeek, getDay, locales })
const tz = Intl.DateTimeFormat().resolvedOptions().timeZone

function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

export default function App() {
  const [allEvents, setAllEvents]                   = useState([])
  const [categories, setCategories]                 = useState(['All'])
  const [category, setCategory]                     = useState('All')
  const [selectedByCategory, setSelectedByCategory] = useState({})
  const [selectedEvent, setSelectedEvent] = useState(null)

  // NEW: control visible date (the calendar will center this date)
  const [currentDate, setCurrentDate] = useState(new Date())

  const selectedGenres = selectedByCategory[category] || []

  const genresForCurrentCategory = Array.from(
    new Set(
      allEvents
        .filter(e => category === 'All' || e.category === category)
        .map(e => e.genre)
    )
  ).filter(Boolean)

  function toggleGenre(genre) {
    setSelectedByCategory(prev => {
      const current = prev[category] || []
      const next = current.includes(genre)
        ? current.filter(g => g !== genre)
        : [...current, genre]
      return { ...prev, [category]: next }
    })
  }

  const API = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    const fetchEvents = async () => {
      const eventsUrl = `${API}/api/events`;

      // 1. try to load existing events
      let list = [];
      try {
        const { data } = await axios.get(eventsUrl);
        list = Array.isArray(data) ? data : [];
      } catch (e) {
        console.log("Initial fetch failed:", e.message);
      }

      // 2. if empty → trigger backend ingest
      if (!list || list.length === 0) {
        console.log("DB empty → triggering Ticketmaster fetch");

        try {
          await axios.post(`${API}/api/admin/fetch-events`);
        } catch (e) {
          console.log("Fetch-events failed:", e.message);
        }

        // 3. fetch again after ingest
        try {
          const { data } = await axios.get(eventsUrl);
          list = Array.isArray(data) ? data : [];
        } catch (e) {
          console.log("Second fetch failed:", e.message);
        }
      }

      // 4. map to calendar format
      const mapped = list
        .filter(evt => evt?.startTime && evt?.endTime)
        .map(evt => {
          const utcStart = parseISO(evt.startTime);
          const utcEnd   = parseISO(evt.endTime);
          return {
            id:       evt.id,
            title:    evt.title,
            venue:    evt.venueName || evt.venue_name,
            url:      evt.url,
            category: evt.category || 'Uncategorized',
            genre:    evt.genre,
            start:    toZonedTime(utcStart, tz),
            end:      toZonedTime(utcEnd,   tz),
            allDay:   false,
            color:    CATEGORY_COLORS[evt.category] || CATEGORY_COLORS['Uncategorized'],
          };
        });

      setAllEvents(mapped);
    };
    fetchEvents();
  }, [API]);



   // Recompute categories whenever events change; reset invalid selection
   useEffect(() => {
     // avoid re-creating Set work if you like
     // import { useMemo } from 'react'
     const catsFromData = Array.from(new Set(allEvents.map(e => e.category))).filter(Boolean);


     setCategories(['All', ...catsFromData]);

     if (category !== 'All' && !catsFromData.includes(category)) {
       setCategory('All');
     }
   }, [allEvents, category]);

  // Client-side filtering
  const filteredEvents = allEvents.filter(evt => {
    const categoryMatch = category === 'All' || evt.category === category
    const genreMatch    = selectedGenres.length === 0 || selectedGenres.includes(evt.genre)
    return categoryMatch && genreMatch
  })

  // NAVIGATION helpers (week +/- 1)
  function goPrevWeek() { setCurrentDate(prev => addWeeks(prev, -1)) }
  function goNextWeek() { setCurrentDate(prev => addWeeks(prev, 1)) }
  function goToday() { setCurrentDate(new Date()) }

  // ----------- dynamic min/max/scrollToTime based on filteredEvents -------------
  const DEFAULT_MIN_HOUR = 8   // fallback start hour
  const DEFAULT_MAX_HOUR = 22  // fallback end hour
  const HOUR_BUFFER = 1        // extra hour padding

  let earliestHour = 24
  let latestHour = -1

  filteredEvents.forEach(evt => {
    if (evt.start instanceof Date) {
      const sh = evt.start.getHours()
      earliestHour = Math.min(earliestHour, sh)
    }
    if (evt.end instanceof Date) {
      const eh = evt.end.getHours()
      if (evt.end.getMinutes() > 0) latestHour = Math.max(latestHour, eh + 1)
      else latestHour = Math.max(latestHour, eh)
    }
  })

  const minHour = earliestHour <= 23 ? clamp(earliestHour - HOUR_BUFFER, 0, 23) : DEFAULT_MIN_HOUR
  const maxHour = latestHour >= 0 ? clamp(latestHour + HOUR_BUFFER, 1, 24) : DEFAULT_MAX_HOUR

  const dayForMinMax = new Date(currentDate) // use currently-centered day
  const minDate = new Date(dayForMinMax)
  minDate.setHours(minHour, 0, 0, 0)

  const maxDate = new Date(dayForMinMax)
  if (maxHour >= 24) {
    maxDate.setHours(23, 59, 59, 999)
  } else {
    maxDate.setHours(maxHour, 0, 0, 0)
  }

  const scrollToTime = minDate
  // ---------------------------------------------------------------------------

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      <div style={{ flex: 1, padding: 20 }}>
        {/* Logo + heading + nav controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <img src={planitLogo} alt="PlanIT" style={{ width: 64, height: 64, borderRadius: 10 }} />
          <h1 style={{ margin: 0 }}>PlanIT Weekly Calendar</h1>

          {/* Buttons row */}
          <div style={{ marginLeft: 20, display: 'flex', gap: 8 }}>
            <button onClick={goToday} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', background: '#fff' }}>Today</button>
            <button onClick={goPrevWeek} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', background: '#fff' }}>Back</button>
            <button onClick={goNextWeek} style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', background: '#fff' }}>Next</button>
          </div>
        </div>

       <Calendar
         localizer={localizer}
         events={filteredEvents}
         defaultView={Views.WEEK}
         views={[Views.WEEK]}
         step={30}
         showMultiDayTimes
         date={currentDate}
         onNavigate={date => setCurrentDate(date)}
         toolbar={false}
         min={minDate}
         max={maxDate}
         scrollToTime={scrollToTime}
         style={{ height: '90%' }}
         onSelectEvent={(event) => setSelectedEvent(event)}
         eventPropGetter={(event) => ({
           style: {
             backgroundColor: event.color,
             borderRadius: '8px',
             border: 'none',
             color: '#fff',
             padding: '4px 6px'
           }
         })}
         components={{
           event: ({ event }) => (
             <div style={{
               fontWeight: 'bold',
               fontSize: '14px',
               lineHeight: '1.2',
               overflow: 'hidden',
               textOverflow: 'ellipsis',
               whiteSpace: 'nowrap'
             }}>
               {event.title}
             </div>
           )
         }}
       />

      {selectedEvent && (
        <div
          onClick={() => setSelectedEvent(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 12,
              width: 360,
              boxShadow: '0 10px 30px rgba(0,0,0,0.25)'
            }}
          >
            <h2 style={{ marginTop: 0 }}>{selectedEvent.title}</h2>

            <p><strong>Category:</strong> {selectedEvent.category}</p>
            <p><strong>Genre:</strong> {selectedEvent.genre || 'N/A'}</p>
            <p><strong>Venue:</strong> {selectedEvent.venue || 'N/A'}</p>

            <p>
              <strong>Time:</strong>{' '}
              {format(selectedEvent.start, 'eee, MMM d, h:mm a')} – {format(selectedEvent.end, 'h:mm a')}
            </p>

            {selectedEvent.url ? (
              <a
                href={selectedEvent.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'inline-block',
                  marginTop: 12,
                  padding: '10px 14px',
                  background: BRAND_YELLOW,
                  color: BRAND_TEXT_DARK,
                  borderRadius: 8,
                  textDecoration: 'none',
                  fontWeight: 'bold'
                }}
              >
                View Event
              </a>
            ) : (
              <p style={{ color: '#666' }}>No event link available.</p>
            )}

            <button
              onClick={() => setSelectedEvent(null)}
              style={{
                display: 'block',
                marginTop: 16,
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #ddd',
                background: '#fff',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      </div>

      <div style={{ width: 260, borderLeft: '1px solid #ddd', padding: 20, boxSizing: 'border-box' }}>
        <h2>Category</h2>
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          style={{ width: '100%', marginBottom: 16 }}
        >
          {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </select>

        <h3>Genres</h3>
        {genresForCurrentCategory.length === 0 && <div style={{color:'#666'}}>No genres for this selection.</div>}
        {genresForCurrentCategory.map(genre => {
          const isSelected = selectedGenres.includes(genre)

          const genreCategory =
            category === 'All'
              ? allEvents.find(e => e.genre === genre)?.category
              : category

          const genreButtonColor =
            CATEGORY_COLORS[genreCategory] || CATEGORY_COLORS.Uncategorized

          const genreTextColor =
            genreCategory === 'Arts & Theatre' ? BRAND_TEXT_DARK : '#fff'

          return (
            <button
              key={genre}
              onClick={() => toggleGenre(genre)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                margin: '8px 0',
                padding: '10px 12px',
                background: genreButtonColor,
                color: genreTextColor,
                opacity: isSelected ? 1 : 0.75,
                border: isSelected ? '2px solid #111827' : 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: isSelected ? 'bold' : 'normal'
              }}
            >
              {genre}
            </button>
          )
        })}
      </div>
    </div>
  )
}
